import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Download, FileText, Link2, Upload, Sparkles, X, Check, ChevronDown, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { EXPERIENCE, ROLES, JD_PRESETS, IMAGES } from "../data/content";
import { downloadRoleResume, downloadTailoredResume } from "../lib/resumePdf";
import { Eyebrow, Reveal, errMsg } from "./shared";
import { OrbitRing, PlanetDot, AstrolabeGlyph } from "./motifs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Reading the job description", "Matching your real experience", "Reframing in your voice", "Composing the resume"];

function ProcessingModal({ open, phase, result, role, onClose, onDownload }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { if (open) { setEmail(""); setSent(false); } }, [open, result]);
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const sendEmail = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Please enter a valid email.");
    setSending(true);
    try {
      const ts = result.targeted_summary || {};
      await axios.post(`${API}/email-resume`, {
        recipient_email: email, role, match_score: result.match_score,
        headline: ts.headline || "", summary: ts.summary || "", highlights: ts.highlights || [],
      });
      setSent(true);
      toast.success("Sent! Check your inbox.");
    } catch (e) {
      toast.error(errMsg(e, "Could not send the email."));
    } finally { setSending(false); }
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="tailor-modal">
          <div className="fixed inset-0 bg-warmdark/60 backdrop-blur-sm" onClick={result ? onClose : undefined} />
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} className="relative bg-ivory rounded-[1.4rem] w-full max-w-lg border border-border p-7 sm:p-9" role="dialog" aria-modal="true">
            {result && <button onClick={onClose} aria-label="Close" data-testid="tailor-close" className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full hover:bg-linen text-caramel"><X size={20} /></button>}
            {!result ? (
              <div>
                <div className="font-mono-tech text-[11px] tracking-widest uppercase text-terracotta mb-6">Tailoring in progress</div>
                <div className="space-y-4">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex items-center gap-3">
                      <span className={`w-7 h-7 grid place-items-center rounded-full text-xs transition-colors ${i < phase ? "bg-sage text-forest" : i === phase ? "bg-terracotta text-ivory" : "bg-linen text-warmmuted"}`}>
                        {i < phase ? <Check size={15} /> : i + 1}
                      </span>
                      <span className={`${i <= phase ? "text-caramel" : "text-warmmuted"}`}>{s}</span>
                      {i === phase && <span className="ml-auto"><span className="inline-block w-4 h-4 border-2 border-terracotta border-t-transparent rounded-full animate-spin" /></span>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div data-testid="tailor-results">
                <div className="flex items-center gap-4">
                  <div className="relative w-24 h-24 shrink-0">
                    <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F2E8DC" strokeWidth="2.5" />
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#B8622A" strokeWidth="2.5" strokeDasharray={`${result.match_score}, 100`} strokeLinecap="round" />
                      {/* orbit tick marks */}
                      {Array.from({ length: 24 }).map((_, i) => {
                        const a = (i * Math.PI) / 12;
                        return <line key={`tick-${i}`} x1={18 + Math.cos(a) * 12.5} y1={18 + Math.sin(a) * 12.5} x2={18 + Math.cos(a) * 13.5} y2={18 + Math.sin(a) * 13.5} stroke="#B08830" strokeWidth="0.4" />;
                      })}
                    </svg>
                    <div className="absolute inset-0 grid place-items-center flex-col">
                      <div className="font-display text-3xl text-terracotta leading-none">{result.match_score}%</div>
                      <div className="font-mono-tech text-[8px] tracking-[0.24em] text-brass mt-0.5">MATCH</div>
                    </div>
                  </div>
                  <div>
                    <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-brass mb-1">Signal locked</div>
                    <h3 className="font-display text-2xl text-caramel font-light">Your tailored match</h3>
                    <p className="text-sm text-caramel/70">{result.targeted_summary?.headline}</p>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted mb-2">Matched keywords</div>
                  <div className="flex flex-wrap gap-2">
                    {(result.matched_keywords || []).map((k) => <span key={k} className="font-mono-tech text-xs rounded-full px-3 py-1.5 bg-sage/20 border border-sage/40 text-forest">{k}</span>)}
                  </div>
                </div>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <button onClick={() => onDownload("summary")} data-testid="download-summary" className="inline-flex items-center justify-center gap-2 rounded-full border border-terracotta text-terracotta px-5 py-3 hover:bg-terracotta hover:text-ivory transition-colors">
                    <Download size={17} /> 1-page Summary
                  </button>
                  <button onClick={() => onDownload("full")} data-testid="download-full" className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-ivory px-5 py-3 hover:bg-warmdark transition-colors">
                    <Download size={17} /> Full Resume
                  </button>
                </div>
                <div className="mt-5 pt-5 border-t border-border">
                  <div className="font-mono-tech text-[11px] tracking-widest uppercase text-warmmuted mb-2 flex items-center gap-2"><Mail size={13} /> Email this summary to yourself</div>
                  {sent ? (
                    <p className="text-forest flex items-center gap-2" data-testid="email-sent"><Check size={16} /> Sent to {email}.</p>
                  ) : (
                    <div className="flex gap-2">
                      <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@company.com" data-testid="email-resume-input" className="flex-1 rounded-full border border-border bg-linen/50 px-4 py-2.5 text-caramel outline-none focus:border-terracotta" />
                      <button onClick={sendEmail} disabled={sending} data-testid="email-resume-send" className="inline-flex items-center gap-1.5 rounded-full bg-gold text-caramel px-5 py-2.5 font-semibold hover:bg-honey transition-colors disabled:opacity-60">
                        <Send size={15} /> {sending ? "Sending" : "Send"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Experience() {
  const [role, setRole] = useState("");
  const [tab, setTab] = useState("text");
  const [jd, setJd] = useState("");
  const [url, setUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState(0);
  const [result, setResult] = useState(null);

  const { core, additional } = useMemo(() => {
    const core = EXPERIENCE.filter((e) => !e.additional);
    const additional = EXPERIENCE.filter((e) => e.additional);
    if (role) {
      core.sort((a, b) => (b.roles.includes(role) ? 1 : 0) - (a.roles.includes(role) ? 1 : 0));
    }
    return { core, additional };
  }, [role]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/parse-jd`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJd(r.data.text || "");
      toast.success("Job description loaded from file.");
    } catch (err) {
      toast.error(errMsg(err, "Could not read that file."));
      setFileName("");
    }
  };

  const generate = async () => {
    const payload = { role };
    if (tab === "link") payload.jd_url = url;
    else payload.jd_text = jd;
    if (tab !== "link" && jd.trim().length < 30) return toast.error("Please paste a job description first (a few sentences).");
    if (tab === "link" && !url.trim()) return toast.error("Please paste a job link.");

    setResult(null); setOpen(true); setPhase(0);
    const timers = [1, 2, 3].map((i) => setTimeout(() => setPhase(i), i * 900));
    try {
      const r = await axios.post(`${API}/tailor-resume`, payload);
      timers.forEach(clearTimeout);
      setPhase(4);
      setTimeout(() => setResult(r.data), 500);
    } catch (e) {
      timers.forEach(clearTimeout);
      setOpen(false);
      toast.error(errMsg(e, "Could not tailor the resume. Please try again."));
    }
  };

  return (
    <section id="experience" className="relative py-24 sm:py-32 bg-ivory overflow-hidden">
      {/* faint hairline grid backdrop */}
      <div className="pointer-events-none absolute inset-0 hairline-grid opacity-25" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow n={4} className="mb-10">Career Journey</Eyebrow></Reveal>

        {/* resume generator — "Analysis Terminal" */}
        <Reveal>
          <div className="relative rounded-[1.6rem] bg-warmdark text-ivory p-6 sm:p-10 grid lg:grid-cols-2 gap-8 overflow-hidden observatory-frame" data-testid="resume-generator">
            {/* on-brand background image, ~35% visible */}
            <div
              className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.35] mix-blend-soft-light"
              style={{ backgroundImage: `url(${IMAGES.about})` }}
              aria-hidden
            />
            {/* warm gradient overlay so text stays readable at the edges */}
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-warmdark/70 via-warmdark/40 to-warmdark/75"
              aria-hidden
            />
            {/* orbit ring, top-right */}
            <div className="pointer-events-none absolute -right-32 -top-32 opacity-70" aria-hidden>
              <div className="orbit-slow">
                <OrbitRing size={420} strokeOpacity={0.55} ticks />
              </div>
            </div>
            <div className="pointer-events-none absolute inset-0 constellation-grid opacity-10" aria-hidden />

            {/* terminal header */}
            <div className="col-span-full flex items-center justify-between gap-4 -mt-2 mb-1 relative">
              <div className="flex items-center gap-3">
                <AstrolabeGlyph size={16} color="#E0B84C" />
                <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-honey">
                  Analysis Terminal · Resume Tailor
                </span>
              </div>
              <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-honey/60 hidden sm:inline">
                Session · Ω-{Math.random().toString(36).slice(2, 6).toUpperCase()}
              </span>
            </div>

            {/* role filter */}
            <div className="relative">
              <div className="font-mono-tech text-[11px] tracking-widest uppercase text-honey mb-3 flex items-center gap-2">
                <PlanetDot /> I'm hiring for a...
              </div>
              <div className="relative">
                <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="role-select" className="w-full appearance-none rounded-full bg-ivory text-caramel px-5 py-3.5 pr-11 outline-none">
                  <option value="">Select a role</option>
                  {ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
                <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-caramel pointer-events-none" />
              </div>
              <button
                onClick={() => { downloadRoleResume(role); toast.success("Resume downloaded."); }}
                disabled={!role}
                data-testid="role-download"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-ivory px-5 py-3.5 font-medium hover:bg-gold hover:text-caramel transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download size={17} /> {role ? `Download ${role} resume` : "Select a role to download"}
              </button>
              <p className="mt-3 text-ivory/60 text-sm">Cards on the right re-sort to surface your most relevant experience.</p>
            </div>

            {/* JD tailoring */}
            <div className="relative lg:border-l lg:border-honey/20 lg:pl-8">
              <div className="font-mono-tech text-[11px] tracking-widest uppercase text-honey mb-3 flex items-center gap-2"><Sparkles size={13} /> Tailor to a job description</div>
              <div className="flex gap-1.5 mb-3 flex-wrap">
                {[["text", "Paste Text", FileText], ["link", "Job Link", Link2], ["upload", "Upload", Upload]].map(([k, label, Icon]) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-mono-tech tracking-wide transition-colors ${tab === k ? "bg-ivory text-caramel" : "bg-ivory/10 text-ivory/80 hover:bg-ivory/20"}`}>
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>
              {tab === "text" && <textarea value={jd} onChange={(e) => setJd(e.target.value)} rows={4} data-testid="jd-text" placeholder="Paste the job description here..." className="w-full rounded-xl bg-ivory/95 text-caramel px-4 py-3 outline-none resize-none placeholder:text-warmmuted/70" />}
              {tab === "link" && <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="jd-url" placeholder="https://company.com/careers/role" className="w-full rounded-xl bg-ivory/95 text-caramel px-4 py-3 outline-none placeholder:text-warmmuted/70" />}
              {tab === "upload" && (
                <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-ivory/30 py-8 cursor-pointer hover:border-honey transition-colors" data-testid="jd-upload-label">
                  <Upload size={22} className="text-honey" />
                  <span className="text-sm text-ivory/80">{fileName || "Upload a PDF, DOCX, or text job description"}</span>
                  <input type="file" accept=".txt,.doc,.docx,.md,.rtf,.pdf" onChange={onFile} className="hidden" data-testid="jd-upload" />
                </label>
              )}
              <div className="mt-3">
                <div className="font-mono-tech text-[10px] tracking-widest uppercase text-ivory/55 mb-2">Or tailor against a sample</div>
                <div className="flex flex-wrap gap-2" data-testid="jd-presets">
                  {JD_PRESETS.map((p, i) => (
                    <button
                      key={p.label}
                      data-testid={`preset-${i}`}
                      onClick={() => { setTab("text"); setJd(p.text); setRole(p.role); setFileName(""); setUrl(""); toast.success(`Loaded sample: ${p.label}`); }}
                      className="font-mono-tech text-[11px] rounded-full px-3 py-1.5 bg-ivory/10 text-ivory/85 border border-ivory/20 hover:bg-ivory/20 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={generate} data-testid="generate-tailored" data-cosmic="true" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-caramel px-5 py-3.5 font-semibold hover:bg-honey transition-colors">
                <Sparkles size={17} /> Generate Tailored Resume
              </button>
            </div>
          </div>
        </Reveal>

        {/* role cards */}
        <div className="mt-12 grid md:grid-cols-2 gap-6" data-testid="experience-cards">
          <AnimatePresence>
            {core.map((job, i) => {
              const relevant = role && job.roles.includes(role);
              return (
                <motion.div key={job.company} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`rounded-2xl border p-6 sm:p-7 transition-colors ${relevant ? "border-terracotta bg-blush/40" : "border-border bg-linen"}`} data-testid={`exp-card-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-2xl sm:text-3xl text-caramel leading-tight">{job.company}</h3>
                      <div className="font-mono-tech text-[11px] tracking-wide uppercase text-terracotta mt-1">{job.role}</div>
                    </div>
                    {relevant && <span className="shrink-0 font-mono-tech text-[10px] tracking-widest uppercase bg-terracotta text-ivory rounded-full px-2.5 py-1">Most relevant</span>}
                  </div>
                  <div className="font-mono-tech text-xs text-warmmuted mt-2">{job.dates} · {job.industry}</div>
                  <ul className="mt-4 space-y-2.5">
                    {job.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-caramel/85 text-[15px] leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-olive shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Additional Projects divider + compact cards */}
        {additional.length > 0 && (
          <Reveal>
            <div className="relative mt-16 mb-7 flex items-center gap-5" data-testid="additional-divider" aria-label="Additional projects">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-honey/45 to-honey/55" aria-hidden />
              <div className="flex items-center gap-2.5 shrink-0">
                <AstrolabeGlyph size={13} color="#B08830" />
                <span className="font-mono-tech text-[10px] tracking-[0.36em] uppercase text-brass">
                  Additional Projects
                </span>
                <AstrolabeGlyph size={13} color="#B08830" />
              </div>
              <span className="h-px flex-1 bg-gradient-to-l from-transparent via-honey/45 to-honey/55" aria-hidden />
            </div>
            <p className="text-center text-warmmuted text-sm mb-6 max-w-lg mx-auto">
              Creative and media work alongside the core operations career.
            </p>
          </Reveal>
        )}

        <div className="grid md:grid-cols-3 gap-5" data-testid="additional-cards">
          {additional.map((job, i) => (
            <motion.div
              key={job.company}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="relative rounded-2xl border border-border/70 bg-linen/60 p-5 hover:border-honey/60 transition-colors"
              data-testid={`add-card-${i}`}
            >
              <span className="absolute top-4 right-4 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-brass/70">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h4 className="font-display text-xl text-caramel leading-tight pr-8">{job.company}</h4>
              <div className="font-mono-tech text-[10px] tracking-wide uppercase text-terracotta/85 mt-1.5">{job.role}</div>
              <div className="font-mono-tech text-[11px] text-warmmuted mt-1">{job.dates} · {job.industry}</div>
              <p className="mt-3 text-caramel/75 text-sm leading-relaxed">{job.bullets[0]}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <ProcessingModal open={open} phase={phase} result={result} role={role} onClose={() => setOpen(false)}
        onDownload={(kind) => { downloadTailoredResume(result, kind); toast.success("Resume downloaded."); }} />
    </section>
  );
}
